
import { TrendingUp, Clock, DollarSign, Shield } from "lucide-react";

interface MetricProps {
  icon: React.ReactNode;
  value: string;
  label: string;
  description: string;
}

const MetricCard = ({ icon, value, label, description }: MetricProps) => (
  <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100 flex flex-col items-center text-center h-full">
    <div className="mb-5 w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
      {icon}
    </div>
    <h3 className="text-4xl font-bold text-edgefi-purple mb-2">{value}</h3>
    <p className="text-xl font-semibold text-gray-900 mb-3">{label}</p>
    <p className="text-gray-600">{description}</p>
  </div>
);

export const ValueMetricsSection = () => {
  const metrics = [
    {
      icon: <TrendingUp className="h-8 w-8 text-edgefi-purple" />,
      value: "99.9%",
      label: "System Uptime",
      description: "Your business stays operational with near-perfect system reliability, minimizing costly downtime."
    },
    {
      icon: <Clock className="h-8 w-8 text-edgefi-purple" />,
      value: "24/7",
      label: "Continuous Monitoring",
      description: "Your systems are watched around the clock, with issues often resolved before you notice them."
    },
    {
      icon: <DollarSign className="h-8 w-8 text-edgefi-purple" />,
      value: "60%",
      label: "Cost Reduction",
      description: "Typical savings compared to hiring in-house IT staff while receiving enterprise-grade support."
    },
    {
      icon: <Shield className="h-8 w-8 text-edgefi-purple" />,
      value: "90%",
      label: "Threat Reduction",
      description: "Reduction in security incidents through proactive management and layered security."
    }
  ];

  return (
    <section className="py-16 bg-gradient-to-b from-purple-50 to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium mb-5">
            The Results You'll See
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900">Your Business Transformation in Numbers</h2>
          <p className="text-xl text-gray-600">
            When you can rely on your technology, your team can focus on serving customers and growing your business.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
          {metrics.map((metric, index) => (
            <MetricCard
              key={index}
              icon={metric.icon}
              value={metric.value}
              label={metric.label}
              description={metric.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
